import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import VoiceCallsApiClient from '../infrastructure/api/VoiceCallsApiClient';
import normalizeVoiceE164 from '../domain/voiceE164';
import {
  hasTenantLinesCache,
  readTenantLinesCache,
  writeTenantLinesCache
} from '../infrastructure/voiceTenantLinesCache';

export { default as normalizeVoiceE164 } from '../domain/voiceE164';

/**
 * @param {Record<string, unknown>} record
 * @returns {string}
 */
function readBusinessE164(record) {
  const candidates = [
    record.businessE164,
    record.business_e164,
    record.from,
    record.phoneNumber,
    record.phone_number
  ];
  const match = candidates.find((value) => typeof value === 'string' && value.trim());
  return normalizeVoiceE164(match || '');
}

function normalizeTenantLine(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */ (raw);
  const businessE164 = readBusinessE164(record);
  if (!businessE164) return null;

  const wabaId = record.wabaPhoneNumberId ?? record.waba_phone_number_id ?? '';
  return {
    businessE164,
    wabaPhoneNumberId: wabaId == null ? '' : String(wabaId),
    enabled: record.enabled !== false
  };
}

/**
 * Loads voice-service tenant WhatsApp lines for the current retailer.
 *
 * @param {{ enabled?: boolean }} [options]
 */
export default function useVoiceTenantLines({ enabled = true } = {}) {
  const retailerId = useSelector((state) => state.retailerUsersReducer?.retailer_info?.id);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || retailerId == null || retailerId === '') {
      setLines([]);
      setLoading(false);
      return undefined;
    }

    if (hasTenantLinesCache(retailerId)) {
      setLines(readTenantLinesCache(retailerId));
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    VoiceCallsApiClient.fetchTenantConfigs()
      .then((items) => {
        if (cancelled) return;
        const normalized = (Array.isArray(items) ? items : [])
          .map(normalizeTenantLine)
          .filter(Boolean);
        writeTenantLinesCache(retailerId, normalized);
        setLines(normalized);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLines([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, retailerId]);

  const enabledLines = useMemo(
    () => lines.filter((line) => line.enabled),
    [lines]
  );

  return {
    lines,
    enabledLines,
    loading,
    error
  };
}
